import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Siren, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inp = "w-full bg-black border border-white/15 rounded px-2 py-1.5 text-white text-xs mt-1";

const SECTION_LABELS = {
  signed_agreement: "Signed Rental Agreement",
  invoice: "Invoice / Charges",
  license_front: "Driver's License — Front",
  license_back: "Driver's License — Back",
  selfie: "Selfie / ID Photo",
  insurance_card: "Insurance Card",
  accident_reports: "Accident / Incident Report + Evidence",
};

export const EmailDetective = ({ booking, token }) => {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("compose");
  const [bundle, setBundle] = useState(null);
  const [form, setForm] = useState({ detective_name: "", detective_email: "", case_number: "" });
  const [selected, setSelected] = useState({});
  const [extras, setExtras] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const openModal = async () => {
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/detective-bundle`, auth);
      const sel = {};
      Object.entries(data.sections).forEach(([k, v]) => { sel[k] = !!v.available; });
      setBundle(data);
      setSelected(sel);
      setForm({
        detective_name: data.saved_contact.detective_name || "",
        detective_email: data.saved_contact.detective_email || "",
        case_number: data.saved_contact.case_number || "",
      });
      setExtras([]);
      setStep("compose");
      setOpen(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load booking documents."); }
  };

  const uploadExtra = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/bookings/${booking.id}/detective-attachment`, fd,
        { headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      setExtras((e) => [...e, data]);
      toast.success(`Added ${data.filename}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed."); }
    finally { setUploading(false); }
  };

  const removeExtra = (idx) => setExtras((e) => e.filter((_, i) => i !== idx));

  const toReview = () => {
    if (!form.detective_name.trim() || !form.detective_email.trim()) {
      toast.error("Detective name and email are required.");
      return;
    }
    if (!Object.values(selected).some(Boolean) && extras.length === 0) {
      toast.error("Select at least one item or add a file to send.");
      return;
    }
    setStep("confirm");
  };

  const confirmSend = async () => {
    setBusy(true);
    try {
      const sections = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
      await axios.post(`${API}/bookings/${booking.id}/email-detective`, {
        detective_name: form.detective_name, detective_email: form.detective_email, case_number: form.case_number,
        sections, extra_attachments: extras.map(({ path, filename }) => ({ path, filename })),
      }, auth);
      toast.success("Case file emailed to detective.");
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send."); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button data-testid={`email-detective-btn-${booking.id}`} onClick={openModal}
        className="inline-flex items-center gap-1.5 text-xs font-bold border border-red-500/60 text-red-300 px-3 py-1.5 rounded-md hover:bg-red-600 hover:text-white transition-colors">
        <Send size={13} /> Email Detective
      </button>
      {open && bundle && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="email-detective-modal">
          <div className="bg-[#111] border border-red-500/40 rounded-lg max-w-lg w-full p-4 space-y-3 max-h-[85vh] overflow-y-auto">
            {step === "compose" ? (
              <>
                <p className="text-red-300 font-black text-sm flex items-center gap-1.5"><Siren size={14} /> Email Detective — {booking.name}</p>
                <div className="rounded border border-white/10 p-2 text-[11px] text-white/60">
                  {booking.vehicle} · {booking.pickup_date} → {booking.return_date} · Booking {booking.id.slice(0, 8)}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-[11px] text-white/50 block">Detective name *
                    <input data-testid="detective-name-input" className={inp} value={form.detective_name}
                      onChange={(e) => setForm((f) => ({ ...f, detective_name: e.target.value }))} placeholder="Det. John Smith" />
                  </label>
                  <label className="text-[11px] text-white/50 block">Case / police report # (optional)
                    <input data-testid="detective-case-input" className={inp} value={form.case_number}
                      onChange={(e) => setForm((f) => ({ ...f, case_number: e.target.value }))} placeholder="PR-12345" />
                  </label>
                </div>
                <label className="text-[11px] text-white/50 block">Detective email *
                  <input data-testid="detective-email-input" className={inp} value={form.detective_email}
                    onChange={(e) => setForm((f) => ({ ...f, detective_email: e.target.value }))} placeholder="detective@department.gov" />
                </label>
                <div className="space-y-1.5">
                  {Object.entries(SECTION_LABELS).map(([k, label]) => {
                    const sec = bundle.sections[k];
                    return (
                      <label key={k} className={`flex items-start gap-2 text-[11px] p-1.5 rounded border cursor-pointer ${sec?.available ? "border-white/10" : "border-white/5 opacity-60"}`}>
                        <input type="checkbox" data-testid={`detective-section-${k}`} disabled={!sec?.available}
                          checked={!!selected[k]} onChange={(e) => setSelected((s) => ({ ...s, [k]: e.target.checked }))} />
                        <span><b className="text-white/85">{label}</b><br />
                          <span className={sec?.available ? "text-white/40" : "text-amber-400"}>{sec?.preview}</span></span>
                      </label>
                    );
                  })}
                </div>
                <div className="border-t border-white/10 pt-2">
                  <p className="text-[10px] uppercase tracking-widest text-white/35 mb-1.5">Additional / replacement files</p>
                  {extras.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-[11px] text-white/60 mb-1">
                      <span>{f.filename}</span>
                      <button data-testid={`detective-remove-extra-${i}`} onClick={() => removeExtra(i)} className="text-red-400 hover:text-red-300 font-bold">Remove</button>
                    </div>
                  ))}
                  <label className="text-[11px] font-bold border border-white/20 text-white/60 px-2.5 py-1.5 rounded cursor-pointer inline-block hover:border-white/40">
                    {uploading ? "Uploading…" : "+ Add File"}
                    <input data-testid="detective-file-input" type="file" className="hidden" disabled={uploading}
                      onChange={(e) => { if (e.target.files[0]) uploadExtra(e.target.files[0]); e.target.value = ""; }} />
                  </label>
                </div>
                <div className="flex gap-2 justify-end pt-1">
                  <button data-testid="detective-cancel" onClick={() => setOpen(false)}
                    className="text-[11px] font-bold border border-white/20 text-white/60 px-3 py-1.5 rounded hover:border-white/40">Cancel</button>
                  <button data-testid="detective-review" onClick={toReview}
                    className="text-[11px] font-bold bg-red-600 text-white px-3 py-1.5 rounded hover:bg-red-500">Preview & Review</button>
                </div>
              </>
            ) : (
              <>
                <p className="text-red-300 font-black text-sm flex items-center gap-1.5"><Siren size={14} /> Confirm Send</p>
                <p className="text-white/70 text-[12px]">Subject: <b className="text-white">Case File — {booking.name} — {booking.vehicle}</b></p>
                <p className="text-white/70 text-[12px]">To: <b className="text-white">{form.detective_name}</b> &lt;{form.detective_email}&gt;
                  {form.case_number ? ` · Case #${form.case_number}` : ""}</p>
                <p className="text-white/60 text-[11px]">This email will include:</p>
                <ul className="text-[11px] text-white/60 list-disc pl-4 space-y-0.5">
                  {Object.entries(selected).filter(([, v]) => v).map(([k]) => <li key={k}>{SECTION_LABELS[k]}</li>)}
                  {extras.map((f, i) => <li key={`x${i}`}>{f.filename} (added file)</li>)}
                </ul>
                <p className="text-red-300/70 text-[10px]">This shares sensitive personal information with a third party. Confirm the recipient is correct before sending.</p>
                <div className="flex gap-2 justify-end pt-1">
                  <button data-testid="detective-back" onClick={() => setStep("compose")}
                    className="text-[11px] font-bold border border-white/20 text-white/60 px-3 py-1.5 rounded hover:border-white/40">Back</button>
                  <button data-testid="detective-confirm-send" disabled={busy} onClick={confirmSend}
                    className="text-[11px] font-bold bg-red-600 text-white px-3 py-1.5 rounded disabled:opacity-50 hover:bg-red-500">
                    {busy ? "Sending…" : "Confirm & Send"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default EmailDetective;
